// Belege – Datenformen (Zod) und die Fabrik für den Standard-Vertragssatz einer Belegart.
//
//   export const invoice = defineDocumentContracts({
//     module: 'sales', entity: 'invoice', documentType: 'invoice', title: 'Rechnung', titlePlural: 'Rechnungen',
//     permissions: { read: 'sales.invoice.read', write: 'sales.invoice.write', post: 'sales.invoice.post', reverse: 'sales.invoice.reverse' },
//     derivesFrom: ['delivery_note'],
//   })
//   → invoice.get · list · saveDraft · post · reverse · derive · transition · print · discard   (Verträge 'sales.invoice.get' …)
//
// Der Server bindet sie mit defineDocumentActions(invoice, invoiceType) aus '@kernel' an den Belegkern.
// Mengen in Tausendstel des Gebindes, Beträge in Cent, Sätze in Basispunkten – wie überall.
import { z } from 'zod'
import { PERMISSION_COSTS_READ, defineContract, type ActionContract, type PermissionKey } from './contract'
import { divRound, toSafeNumber } from './decimal'
import { zId } from './ids'
import { listInput, listOutput } from './list'
import { percentOf, type Cents, type RateBp } from './money'
import type { Qty } from './qty'
import { documentTotals, type PriceMode } from './tax'
import { TAX_CASES } from './taxCase'

export const DOCUMENT_LINE_KINDS = ['item', 'text', 'fee', 'discount', 'deposit', 'deposit_return', 'rounding'] as const
export type DocumentLineKind = (typeof DOCUMENT_LINE_KINDS)[number]
/** Zeilenarten, die ein Aufrufer erfassen darf ('rounding' erzeugt nur der Kern). */
export const DOCUMENT_INPUT_LINE_KINDS = ['item', 'text', 'fee', 'discount', 'deposit', 'deposit_return'] as const

export const PRICE_MODES = ['gross', 'net'] as const

const zDate = z.iso.date()
const zText = (max: number) => z.string().trim().max(max)
const zCustomFields = z.record(z.string(), z.unknown())

/** Kopie einer Anschrift im Beleg (Lieferanschrift, Rechnungsanschrift im Partner-Snapshot). */
export const zDocumentAddress = z.object({
  name: z.string().nullable(),
  street: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  note: z.string().nullable().optional(),
})
export type DocumentAddress = z.output<typeof zDocumentAddress>

// ---- Eingabe -----------------------------------------------------------------------------------------

export const zDocumentLineInput = z.strictObject({
  /** vorhandene Zeile (behält ihre Kennung – wichtig für Reservierung und Belegkette); ohne = neue Zeile */
  id: zId.optional(),
  kind: z.enum(DOCUMENT_INPUT_LINE_KINDS).default('item'),
  itemId: zId.nullish(),
  /** Gebinde des Artikels (item_unit.id); ohne Angabe das Verkaufs- bzw. Einkaufs-Standardgebinde */
  unitId: zId.nullish(),
  description: zText(500).nullish(),
  /**
   * Menge im Gebinde in Tausendstel (1 Kasten = 1000); bei Pfandzeilen Stück der Pfandart. Das Vorzeichen setzt
   * der Kern nach der Zeilenart (Leergutrückgabe und Rabatt mindern) – gesendet wird der Betrag; ein Minus aus
   * der Ansicht wird hingenommen.
   */
  qty: z.int().min(-1_000_000_000).max(1_000_000_000).default(1000),
  /** manueller Einzelpreis in der Basis des Belegs – ohne Angabe schlägt die Preisfindung vor */
  unitPriceCents: z.int().min(-100_000_000).max(100_000_000).nullish(),
  /** Preiseinheit in Tausendstel (1000 = je Gebinde, 100_000 = je 100) */
  pricePerQty: z.int().min(1).max(100_000_000).nullish(),
  discountBp: z.int().min(0).max(10_000).default(0),
  /** überschreibt den Steuerschlüssel des Artikels; Pflicht bei Gebühr und Rabatt ohne Artikel */
  taxCodeId: zId.nullish(),
  /** Pfandzeilen: Pfandart (Blatt oder Gebinde) */
  depositTypeId: zId.nullish(),
  /**
   * Leergutrückgabe eines Gebindes mit abweichendem Inhalt („Kasten mit 17 Flaschen“, Mischkasten): tatsächlicher
   * Inhalt ALLER Gebinde der Zeile in Blatt-Pfandarten, Stück in Tausendstel. Ohne Angabe gilt der volle Inhalt.
   */
  depositContents: z.array(z.strictObject({ depositTypeId: zId, qty: z.int().min(0).max(1_000_000_000) })).max(20).nullish(),
  lotId: zId.nullish(),
  /** Wareneingang: Charge per Nummer anlegen bzw. wiederverwenden */
  lotNo: zText(80).nullish(),
  bestBefore: zDate.nullish(),
  /** Seriennummern der Stücke dieser Zeile – eine je Stück, nur bei Artikeln mit Seriennummernpflicht */
  serialNos: z.array(zText(80)).max(500).nullish(),
  binId: zId.nullish(),
  /** Vorgängerzeile der Belegkette (setzt derive selbst) */
  sourceLineId: zId.nullish(),
  reasonCodeId: zId.nullish(),
  note: zText(500).nullish(),
  customFields: zCustomFields.default({}),
})
export type DocumentLineInput = z.input<typeof zDocumentLineInput>
export type DocumentLineData = z.output<typeof zDocumentLineInput>

const headerShape = {
  locationId: zId,
  targetLocationId: zId.nullish(),
  partnerId: zId.nullish(),
  shippingAddressId: zId.nullish(),
  paymentTermId: zId.nullish(),
  /** Belegdatum; Standard: heute (Geschäftstag) */
  documentDate: zDate.optional(),
  /** Leistungsdatum bzw. Beginn des Leistungszeitraums – bestimmt den Steuersatz */
  serviceDate: zDate.nullish(),
  serviceDateTo: zDate.nullish(),
  deliveryDate: zDate.nullish(),
  /** brutto oder netto; ohne Angabe nach Belegart bzw. Preisgruppe des Partners */
  priceMode: z.enum(PRICE_MODES).optional(),
  /** Rabatt auf den Warenwert des ganzen Belegs (ohne Pfand) */
  headerDiscountBp: z.int().min(0).max(10_000).default(0),
  externalRef: zText(120).nullish(),
  buyerReference: zText(120).nullish(),
  notes: zText(4000).nullish(),
  internalNotes: zText(4000).nullish(),
  reasonCodeId: zId.nullish(),
  reason: zText(500).nullish(),
  customFields: zCustomFields.default({}),
}

/** Kopf und Zeilen eines Entwurfs – immer der ganze Beleg (Zeilen ohne `id` entstehen neu, fehlende entfallen). */
export const zDocumentDraftInput = z.strictObject({
  /** vorhandener Entwurf; ohne = neu anlegen */
  id: zId.optional(),
  /** Version des geladenen Stands (optimistische Sperre) */
  version: z.int().optional(),
  ...headerShape,
  lines: z.array(zDocumentLineInput).max(500).default([]),
})
export type DocumentDraftInput = z.input<typeof zDocumentDraftInput>
export type DocumentDraftData = z.output<typeof zDocumentDraftInput>

export const zDocumentDeriveInput = z.strictObject({
  sources: z
    .array(
      z.strictObject({
        documentId: zId,
        /** Teilmengen je Zeile im Gebinde der Zeile; ohne Angabe alle Zeilen mit ihrer offenen Restmenge */
        lines: z.array(z.strictObject({ lineId: zId, qty: z.int().positive() })).optional(),
      }),
    )
    .min(1)
    .max(100),
  documentDate: zDate.optional(),
  serviceDate: zDate.nullish(),
  notes: zText(4000).nullish(),
})
export type DocumentDeriveInput = z.input<typeof zDocumentDeriveInput>

// ---- Ausgabe ------------------------------------------------------------------------------------------

export const zDocumentPriceSource = z.object({
  /** customer | promotion | group | base | purchase | manual | source | deposit | header_discount */
  kind: z.string(),
  label: z.string(),
  manual: z.boolean(),
})

export const zDocumentLineView = z.object({
  id: zId,
  pos: z.int(),
  kind: z.enum(DOCUMENT_LINE_KINDS),
  itemId: zId.nullable(),
  unitId: zId.nullable(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  qty: z.int(),
  baseQty: z.int(),
  /** Kurzform des Gebindes: „Ka“ */
  unit: z.string().nullable(),
  /** UN/ECE-Code der Einheit (E-Rechnung) */
  unitCode: z.string().nullable(),
  unitPriceCents: z.int(),
  pricePerQty: z.int(),
  discountBp: z.int(),
  netCents: z.int(),
  taxCents: z.int(),
  grossCents: z.int(),
  taxCodeId: zId.nullable(),
  taxRateBp: z.int().nullable(),
  /** Einstandswert zum Buchungszeitpunkt – nur mit dem Recht „Einstandspreise sehen“ */
  costCents: z.int().nullable().optional(),
  depositTypeId: zId.nullable(),
  /** Pfandzeile gehört zu dieser Warenzeile */
  parentLineId: zId.nullable(),
  /** vom Kern erzeugt (Pfand zu einer Warenzeile, Belegrabatt) – beim Speichern nicht mitsenden (wird ignoriert) */
  auto: z.boolean(),
  depositContents: z.array(z.object({ depositTypeId: zId, qty: z.int() })).nullable(),
  lotId: zId.nullable(),
  lotNo: z.string().nullable(),
  /**
   * Ausgang aus mehreren Chargen (FEFO hat die Zeile aufgeteilt): jede Charge mit ihrem Anteil in Basiseinheit.
   * Nur dann gesetzt – `lotId`/`lotNo` sind in diesem Fall leer.
   */
  lots: z.array(z.object({ lotId: zId, lotNo: z.string(), bestBefore: z.string().nullable(), baseQty: z.int() })).optional(),
  /** Seriennummern der Stücke dieser Zeile (nur Artikel mit Seriennummernpflicht) */
  serialNos: z.array(z.string()).optional(),
  bestBefore: z.string().nullable(),
  binId: zId.nullable(),
  /** Fortschritt in Basiseinheit */
  qtyShipped: z.int(),
  qtyReceived: z.int(),
  qtyInvoiced: z.int(),
  qtyReturned: z.int(),
  sourceLineId: zId.nullable(),
  priceSource: zDocumentPriceSource.nullable(),
  /**
   * Was die Preisfindung für diese Zeile vorschlüge (Partner, Standort, Belegdatum, Menge) – nur gesetzt, wenn der
   * Preis von Hand gesetzt ist und der Beleg sich noch ändern lässt. Im Preismodus des Belegs, wie unitPriceCents.
   */
  proposedPrice: z.object({ unitPriceCents: z.int(), pricePerQty: z.int(), label: z.string() }).nullable().optional(),
  /** Grundpreis „2,38 €/l“, wo er auszuweisen ist */
  basePriceText: z.string().nullable(),
  /**
   * Ersatzschlüssel wegen des Steuerfalls des Kunden (AP2c): `fromTaxCodeId` ist der inländische Schlüssel, den der
   * Kern ersetzt hat (z. B. 19 % → innergemeinschaftliche Lieferung). Fehlt bzw. null: kein Ersatz.
   */
  taxCase: z.object({ taxCase: z.enum(TAX_CASES), fromTaxCodeId: zId }).nullable().optional(),
  reasonCodeId: zId.nullable(),
  note: z.string().nullable(),
  customFields: zCustomFields,
})
export type DocumentLineView = z.output<typeof zDocumentLineView>

export const zDocumentTaxView = z.object({
  taxCodeId: zId,
  label: z.string(),
  /** Kategorie nach EN 16931: S, Z, E … */
  category: z.string(),
  rateBp: z.int(),
  netCents: z.int(),
  taxCents: z.int(),
  grossCents: z.int(),
})

export const zDocumentDepositView = z.object({
  lineId: zId.nullable(),
  depositTypeId: zId,
  name: z.string(),
  direction: z.enum(['out', 'in']),
  qty: z.int(),
  unitAmountCents: z.int(),
  amountCents: z.int(),
  taxRateBp: z.int().nullable(),
})

export const zDocumentLinkView = z.object({
  /** derived | reversal | return | payment | collective | correction */
  kind: z.string(),
  /** 'from' = Vorgänger dieses Belegs, 'to' = Nachfolger */
  direction: z.enum(['from', 'to']),
  documentId: zId,
  type: z.string(),
  typeTitle: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  documentDate: z.string(),
})

export const zDocumentEventView = z.object({
  at: z.date(),
  fromStatus: z.string().nullable(),
  toStatus: z.string(),
  toStatusLabel: z.string(),
  by: zId.nullable(),
  byName: z.string().nullable(),
  channel: z.string().nullable(),
  note: z.string().nullable(),
})

export const zDocumentFileView = z.object({ id: zId, fileId: zId, kind: z.string(), sha256: z.string(), createdAt: z.date() })

export const zDocumentOpenItemView = z.object({
  id: zId,
  direction: z.enum(['receivable', 'payable']),
  amountCents: z.int(),
  openCents: z.int(),
  /** open | partial | settled */
  status: z.string(),
  dueDate: z.string().nullable(),
  discountUntil: z.string().nullable(),
  discountBp: z.int(),
  dunningLevel: z.int(),
})

/** SEPA-Lastschriftmandat des Kunden, wie es beim Festschreiben galt (BG-19). */
export const zDocumentSepaMandate = z.object({
  reference: z.string(),
  /** Tag der Unterschrift 'YYYY-MM-DD' */
  signedAt: z.string().nullable(),
  iban: z.string(),
})

export const zDocumentPartnerSnapshot = z.object({
  number: z.string().nullable(),
  name: z.string(),
  name2: z.string().nullable(),
  /** private | business | public */
  type: z.string().nullable(),
  vatId: z.string().nullable(),
  taxNumber: z.string().nullable(),
  email: z.string().nullable(),
  address: zDocumentAddress.nullable(),
  // ---- E-Rechnung (Wunsch einvoice-01, SPEZIFIKATION §4.4): festgeschrieben seit Migration 0091 / 1000. Ältere
  //      Belege tragen die Schlüssel nicht (undefined), bereinigte Belege tragen sie als null (Löschkonzept).
  /** E-Rechnungs-Adresse (BT-49): E-Mail oder `<EAS>:<Kennung>` (§5.3) */
  einvoiceAddress: z.string().nullable().optional(),
  /** xrechnung | zugferd | pdf; null = nach Regel */
  einvoiceFormat: z.string().nullable().optional(),
  /** ubl | cii; null = Vorgabe des Betriebs */
  einvoiceSyntax: z.string().nullable().optional(),
  /** GLN des Kunden (BT-46, Schema 0088) */
  gln: z.string().nullable().optional(),
  /** unsere Lieferantennummer beim Kunden (BT-29) */
  supplierRefAtCustomer: z.string().nullable().optional(),
  /** transfer | direct_debit | cash (BT-81) */
  paymentMethod: z.string().nullable().optional(),
  sepaMandate: zDocumentSepaMandate.nullable().optional(),
  /** Rahmen- oder Bonusvertrag (BT-12) */
  contractRef: z.string().nullable().optional(),
  /** Hinweis auf vereinbarte Entgeltminderungen (BT-22) */
  rebateNote: z.string().nullable().optional(),
  /** Registernummer des Käufers (BT-47) – kommt mit dem Steuerfall je Kunde (AP2c) */
  registerNumber: z.string().nullable().optional(),
  /** gesetzt, wenn das Löschkonzept den Personenbezug entfernt hat */
  anonymized: z.boolean().optional(),
})
export type DocumentPartnerSnapshot = z.output<typeof zDocumentPartnerSnapshot>

export const zDocumentPaymentTerms = z.object({
  key: z.string().nullable(),
  name: z.string(),
  dueDays: z.int(),
  discountDays: z.int().nullable(),
  discountBp: z.int(),
})
export type DocumentPaymentTerms = z.output<typeof zDocumentPaymentTerms>

export const zDocumentView = z.object({
  id: zId,
  type: z.string(),
  typeTitle: z.string(),
  status: z.string(),
  statusLabel: z.string(),
  number: z.string().nullable(),
  posted: z.boolean(),
  postedAt: z.date().nullable(),
  revision: z.int(),
  version: z.int(),
  /** letzte Änderung an Kopf oder Zeilen (auch im Entwurf) – und wer sie gespeichert hat (für „inzwischen geändert“) */
  updatedAt: z.date(),
  updatedBy: zId.nullable(),
  updatedByName: z.string().nullable(),
  locationId: zId,
  targetLocationId: zId.nullable(),
  partnerId: zId.nullable(),
  partner: zDocumentPartnerSnapshot.nullable(),
  shippingAddressId: zId.nullable(),
  shippingAddress: zDocumentAddress.nullable(),
  paymentTermId: zId.nullable(),
  paymentTerms: zDocumentPaymentTerms.nullable(),
  documentDate: z.string(),
  serviceDate: z.string().nullable(),
  serviceDateTo: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  priceMode: z.enum(PRICE_MODES),
  currency: z.string(),
  netCents: z.int(),
  taxCents: z.int(),
  grossCents: z.int(),
  /** Pfandsaldo brutto (berechnet − erstattet), in grossCents enthalten */
  depositCents: z.int(),
  headerDiscountBp: z.int(),
  externalRef: z.string().nullable(),
  buyerReference: z.string().nullable(),
  notes: z.string().nullable(),
  internalNotes: z.string().nullable(),
  reasonCodeId: zId.nullable(),
  reason: z.string().nullable(),
  reversalOfId: zId.nullable(),
  reversedById: zId.nullable(),
  isTraining: z.boolean(),
  customFields: zCustomFields,
  lines: z.array(zDocumentLineView),
  taxes: z.array(zDocumentTaxView),
  deposits: z.array(zDocumentDepositView),
  links: z.array(zDocumentLinkView),
  events: z.array(zDocumentEventView),
  files: z.array(zDocumentFileView),
  openItem: zDocumentOpenItemView.nullable(),
  /** was mit dem Beleg in seinem Zustand möglich ist (ohne Rechteprüfung – die macht der Dispatcher) */
  allowed: z.object({
    edit: z.boolean(),
    post: z.boolean(),
    reverse: z.boolean(),
    revise: z.boolean(),
    transitions: z.array(z.object({ to: z.string(), label: z.string(), requiresReason: z.boolean() })),
    deriveTo: z.array(z.object({ type: z.string(), title: z.string() })),
  }),
})
export type DocumentView = z.output<typeof zDocumentView>

export const zDocumentListRow = z.object({
  id: zId,
  type: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  documentDate: z.string(),
  dueDate: z.string().nullable(),
  locationId: zId,
  partnerId: zId.nullable(),
  partnerName: z.string().nullable(),
  externalRef: z.string().nullable(),
  netCents: z.int(),
  grossCents: z.int(),
  /** offener Betrag des offenen Postens (null = keiner) */
  openCents: z.int().nullable(),
  lineCount: z.int(),
})
export type DocumentListRow = z.output<typeof zDocumentListRow>

export const DOCUMENT_SORT_KEYS = ['documentDate', 'number', 'grossCents', 'partnerName'] as const

export const zDocumentListInput = listInput({
  sortBy: DOCUMENT_SORT_KEYS,
  defaultSort: { by: 'documentDate', dir: 'desc' },
  filters: {
    status: z.array(z.string().max(40)).max(20).optional(),
    partnerId: zId.optional(),
    /** Teilmenge der Standorte, für die das Leserecht gilt */
    locationIds: z.array(zId).max(200).optional(),
    from: zDate.optional(),
    to: zDate.optional(),
    /** nur Belege mit offenem Posten */
    openOnly: z.boolean().optional(),
  },
})
export type DocumentListInput = z.output<typeof zDocumentListInput>

// ---- Druck --------------------------------------------------------------------------------------------

const zPrintParty = z.object({
  name: z.string(),
  lines: z.array(z.string()),
  vatId: z.string().nullable(),
  taxNumber: z.string().nullable(),
})

/** Strukturierte Daten der HTML-Druckvorschau – alles fertig formatiert UND als Rohwert (Cent, Tausendstel). */
export const zDocumentPrintModel = z.object({
  template: z.string(),
  title: z.string(),
  number: z.string().nullable(),
  /** „ENTWURF“, „DEMO“, „STORNO“ … */
  markers: z.array(z.string()),
  sender: zPrintParty.extend({
    legalLine: z.string(),
    contact: z.array(z.string()),
    bank: z.array(z.string()),
    register: z.string().nullable(),
    managingDirectors: z.string().nullable(),
  }),
  recipient: zPrintParty.extend({ number: z.string().nullable() }).nullable(),
  shippingAddress: z.array(z.string()).nullable(),
  meta: z.array(z.object({ label: z.string(), value: z.string() })),
  priceMode: z.enum(PRICE_MODES),
  positions: z.array(
    z.object({
      pos: z.int(),
      kind: z.enum(DOCUMENT_LINE_KINDS),
      sku: z.string().nullable(),
      description: z.string(),
      qty: z.int(),
      qtyText: z.string(),
      unit: z.string().nullable(),
      unitPriceCents: z.int(),
      unitPriceText: z.string(),
      discountText: z.string().nullable(),
      basePriceText: z.string().nullable(),
      taxRateBp: z.int().nullable(),
      taxRateText: z.string().nullable(),
      /** Zeilenbetrag in der Basis des Belegs */
      amountCents: z.int(),
      amountText: z.string(),
      lotText: z.string().nullable(),
      note: z.string().nullable(),
    }),
  ),
  /** Pfand getrennt ausgewiesen: je Pfandzeile zusammengefasst, darunter die Blätter */
  deposit: z.object({
    chargedCents: z.int(),
    refundedCents: z.int(),
    balanceCents: z.int(),
    balanceText: z.string(),
    rows: z.array(z.object({ label: z.string(), direction: z.enum(['out', 'in']), qtyText: z.string(), amountCents: z.int(), amountText: z.string() })),
  }),
  taxes: z.array(z.object({ label: z.string(), rateBp: z.int(), netCents: z.int(), taxCents: z.int(), grossCents: z.int(), netText: z.string(), taxText: z.string(), grossText: z.string() })),
  totals: z.object({
    /** Warenwert ohne Pfand in der Basis des Belegs */
    goodsCents: z.int(),
    netCents: z.int(),
    taxCents: z.int(),
    grossCents: z.int(),
    netText: z.string(),
    taxText: z.string(),
    grossText: z.string(),
  }),
  payment: z.object({ terms: z.string().nullable(), dueDate: z.string().nullable(), discountText: z.string().nullable() }),
  /** Pflicht- und Hinweistexte (§ 14 UStG, § 19, Storno-Bezug, Demo) */
  texts: z.array(z.string()),
  notes: z.string().nullable(),
  /** E-Rechnung (XRechnung/ZUGFeRD) wird in v1 nicht erzeugt – Einhängepunkt documents.registerEInvoiceBuilder() */
  einvoice: z.object({ available: z.boolean(), reason: z.string() }),
  /**
   * Empfangsnachweis (Lieferschein nach der Zustellung, docs/belege-nach-zustellung/SPEZIFIKATION.md §2): wer angenommen
   * hat, wann – und die Unterschrift als Bild aus den Dateien des Belegs (`url` = /api/files/<fileId>). Fehlt bei allen
   * anderen Belegen.
   */
  receipt: z
    .object({
      title: z.string(),
      lines: z.array(z.string()),
      signature: z.object({ fileId: zId, url: z.string(), contentType: z.string() }).nullable(),
    })
    .nullable()
    .optional(),
})
export type DocumentPrintModel = z.output<typeof zDocumentPrintModel>

// ---- Rechnen (rein, läuft auch im Browser – der Entwurfs-Editor zeigt damit dieselben Summen wie der Server) ----

export interface CalcLine {
  kind: DocumentLineKind
  /** Menge mit Vorzeichen, wie gespeichert (Leergutrückgabe in einem Verkaufsbeleg ist negativ) */
  qty: Qty
  /** Einzelpreis in der Basis des Belegs je `pricePerQty` */
  unitPriceCents: Cents
  pricePerQty: Qty
  discountBp: number
  taxCodeId: string | null
  rateBp: RateBp | null
}

export interface CalcLineResult {
  /** Zeilenbetrag in der Basis des Belegs (nach Zeilenrabatt) */
  amountCents: Cents
  netCents: Cents
  taxCents: Cents
  grossCents: Cents
}

export interface CalcTaxGroup {
  taxCodeId: string
  rateBp: RateBp
  netCents: Cents
  taxCents: Cents
  grossCents: Cents
}

export interface CalcResult {
  lines: CalcLineResult[]
  /** je Steuerschlüssel und Satz – die Steuer ist EINMAL je Gruppe gerechnet; Σ Gruppen = Kopf */
  taxes: CalcTaxGroup[]
  netCents: Cents
  taxCents: Cents
  grossCents: Cents
  /** Pfandsaldo brutto (berechnet − erstattet), in grossCents enthalten */
  depositCents: Cents
  /** Warenwert ohne Pfand in der Basis des Belegs */
  goodsCents: Cents
}

const DEPOSIT_KINDS: ReadonlySet<DocumentLineKind> = new Set(['deposit', 'deposit_return'])
export const isDepositLineKind = (kind: DocumentLineKind): boolean => DEPOSIT_KINDS.has(kind)

/** Zeilenbetrag = Menge × Preis ÷ Preiseinheit × (1 − Rabatt) – in EINEM Schritt gerechnet und einmal gerundet. */
export function documentLineAmount(line: Pick<CalcLine, 'qty' | 'unitPriceCents' | 'pricePerQty' | 'discountBp'>): Cents {
  if (line.pricePerQty <= 0) throw new RangeError('Die Preiseinheit muss größer als null sein.')
  if (line.discountBp < 0 || line.discountBp > 10_000) throw new RangeError('Der Rabatt liegt zwischen 0 und 100 %.')
  return toSafeNumber(
    divRound(BigInt(line.qty) * BigInt(line.unitPriceCents) * BigInt(10_000 - line.discountBp), BigInt(line.pricePerQty) * 10_000n),
    'Zeilenbetrag',
  )
}

/**
 * Belegsummen aus den Zeilen. Zeilen ohne Steuerschlüssel (Text) tragen keinen Betrag – sonst RangeError (der
 * Server meldet vorher den fehlenden Steuerschlüssel als Fachfehler). Es gilt je Zeile netto + Steuer = brutto,
 * je Steuergruppe Σ Zeilen = Gruppe und für den Beleg Σ Gruppen = Kopf – auf den Cent.
 */
export function calculateDocument(lines: readonly CalcLine[], mode: PriceMode): CalcResult {
  const amounts = lines.map((line) => (line.kind === 'text' ? 0 : documentLineAmount(line)))
  const taxed: number[] = []
  lines.forEach((line, index) => {
    if (line.taxCodeId !== null && line.rateBp !== null) taxed.push(index)
    else if (amounts[index] !== 0) throw new RangeError('Eine Zeile mit Betrag braucht einen Steuerschlüssel.')
  })
  const totals = documentTotals(
    taxed.map((index) => ({
      amount: amounts[index] as Cents,
      rateBp: (lines[index] as CalcLine).rateBp as RateBp,
      taxCodeId: (lines[index] as CalcLine).taxCodeId,
    })),
    mode,
  )
  const result: CalcLineResult[] = lines.map((_, index) => ({ amountCents: amounts[index] as Cents, netCents: 0, taxCents: 0, grossCents: 0 }))
  taxed.forEach((index, position) => {
    const split = totals.lines[position] as { net: Cents; tax: Cents; gross: Cents }
    Object.assign(result[index] as CalcLineResult, { netCents: split.net, taxCents: split.tax, grossCents: split.gross })
  })
  let depositCents = 0
  let goodsCents = 0
  lines.forEach((line, index) => {
    if (isDepositLineKind(line.kind)) depositCents += (result[index] as CalcLineResult).grossCents
    else goodsCents += amounts[index] as Cents
  })
  return {
    lines: result,
    taxes: totals.groups.map((group) => ({
      taxCodeId: group.taxCodeId as string,
      rateBp: group.rateBp,
      netCents: group.net,
      taxCents: group.tax,
      grossCents: group.gross,
    })),
    netCents: totals.net,
    taxCents: totals.tax,
    grossCents: totals.gross,
    depositCents,
    goodsCents,
  }
}

/**
 * Belegrabatt auf den Warenwert (Artikel- und Rabattzeilen, ohne Pfand und Gebühren): je Steuergruppe EINE
 * Rabattzeile, damit das Entgelt nach Steuersätzen aufgeschlüsselt bleibt (§ 14 Abs. 4 Nr. 7 UStG).
 */
export function headerDiscountGroups(
  lines: readonly Pick<CalcLine, 'kind' | 'qty' | 'unitPriceCents' | 'pricePerQty' | 'discountBp' | 'taxCodeId' | 'rateBp'>[],
  headerDiscountBp: number,
): { taxCodeId: string; rateBp: RateBp; baseCents: Cents; discountCents: Cents }[] {
  if (headerDiscountBp <= 0) return []
  const groups = new Map<string, { taxCodeId: string; rateBp: RateBp; baseCents: Cents }>()
  for (const line of lines) {
    if ((line.kind !== 'item' && line.kind !== 'discount') || line.taxCodeId === null || line.rateBp === null) continue
    const key = `${line.taxCodeId}|${line.rateBp}`
    const group = groups.get(key) ?? { taxCodeId: line.taxCodeId, rateBp: line.rateBp, baseCents: 0 }
    group.baseCents += documentLineAmount(line)
    groups.set(key, group)
  }
  return [...groups.values()]
    .map((group) => ({ ...group, discountCents: percentOf(group.baseCents, headerDiscountBp) }))
    .filter((group) => group.discountCents !== 0)
    .sort((a, b) => b.rateBp - a.rateBp || a.taxCodeId.localeCompare(b.taxCodeId))
}

// ---- Vertragssatz -------------------------------------------------------------------------------------

export interface DocumentContractSpec<M extends string, E extends string> {
  /** Modul-ID (erstes Namenssegment) */
  module: M
  /** Gegenstand in camelCase (zweites Namenssegment): 'invoice', 'deliveryNote' */
  entity: E
  /** Belegart (document.type) */
  documentType: string
  /** deutsch, Einzahl: „Rechnung“ */
  title: string
  /** deutsch, Mehrzahl: „Rechnungen“ */
  titlePlural: string
  permissions: {
    read: PermissionKey
    write: PermissionKey
    post: PermissionKey
    /** Standard: post */
    reverse?: PermissionKey
    /** Standard: write */
    transition?: PermissionKey
    /** Standard: write */
    derive?: PermissionKey
    /** Standard: read */
    print?: PermissionKey
  }
  /** Belegarten, aus denen diese abgeleitet wird – ohne Angabe gibt es keinen derive-Vertrag */
  derivesFrom?: readonly string[]
  feature?: string
  /** Wörter, die Mitarbeiter sagen (für die Werkzeugsuche des Assistenten) */
  keywords?: readonly string[]
  /** Recht für Einstandswerte in der Ausgabe; null = Feld nie liefern. Standard: 'costs.read' */
  costPermission?: PermissionKey | null
  /** Festschreiben bucht Geld oder Bestandswert (Standard: true → Risiko 'financial') */
  financial?: boolean
  /** Verträge für die öffentliche API freigeben (get, list, saveDraft, post) */
  publicApi?: boolean
}

const zIdInput = z.strictObject({ id: zId })
const zPostInput = z.strictObject({ id: zId, version: z.int().optional() })
const zReverseInput = z.strictObject({ id: zId, reason: z.string().trim().min(3).max(500), documentDate: zDate.optional() })
const zTransitionInput = z.strictObject({ id: zId, to: z.string().min(1).max(40), reason: zText(500).nullish() })
const zReverseOutput = z.object({ document: zDocumentView, reversalId: zId.nullable(), reversalNumber: z.string().nullable() })
const zDiscardOutput = z.object({ id: zId })

type C<N extends string, I extends z.ZodType, O extends z.ZodType> = ActionContract<N, I, O>

export interface DocumentContracts<M extends string = string, E extends string = string> {
  readonly documentType: string
  readonly title: string
  readonly titlePlural: string
  /** Rechte je Vorgang mit eingesetzten Vorgaben – defineDocumentActions gleicht sie mit der Belegart ab */
  readonly permissions: Readonly<Record<'read' | 'write' | 'post' | 'reverse' | 'transition' | 'derive' | 'print', PermissionKey>>
  readonly get: C<`${M}.${E}.get`, typeof zIdInput, typeof zDocumentView>
  readonly list: C<`${M}.${E}.list`, typeof zDocumentListInput, ReturnType<typeof listOutput<typeof zDocumentListRow>>>
  readonly saveDraft: C<`${M}.${E}.saveDraft`, typeof zDocumentDraftInput, typeof zDocumentView>
  readonly post: C<`${M}.${E}.post`, typeof zPostInput, typeof zDocumentView>
  readonly reverse: C<`${M}.${E}.reverse`, typeof zReverseInput, typeof zReverseOutput>
  readonly transition: C<`${M}.${E}.transition`, typeof zTransitionInput, typeof zDocumentView>
  readonly print: C<`${M}.${E}.print`, typeof zIdInput, typeof zDocumentPrintModel>
  /** Entwurf verwerfen (löschen) – nur solange nichts festgeschrieben ist */
  readonly discard: C<`${M}.${E}.discard`, typeof zIdInput, typeof zDiscardOutput>
  /** nur wenn derivesFrom angegeben ist */
  readonly derive: C<`${M}.${E}.derive`, typeof zDocumentDeriveInput, typeof zDocumentView> | null
  /** alle vorhandenen Verträge als Liste */
  readonly all: readonly ActionContract[]
}

/** Vertragssatz einer Belegart MIT Vorgängern: derive ist vorhanden. */
export interface DerivableDocumentContracts<M extends string = string, E extends string = string> extends DocumentContracts<M, E> {
  readonly derive: C<`${M}.${E}.derive`, typeof zDocumentDeriveInput, typeof zDocumentView>
}

/**
 * Erzeugt den Standard-Vertragssatz einer Belegart. Standortbezug: Listen 'any' (die Aktion filtert mit den
 * Standorten des Leserechts), alles andere 'record' – der Standort steht im Beleg, nicht in der Eingabe.
 */
export function defineDocumentContracts<const M extends string, const E extends string>(
  spec: DocumentContractSpec<M, E> & { derivesFrom: readonly [string, ...string[]] },
): DerivableDocumentContracts<M, E>
export function defineDocumentContracts<const M extends string, const E extends string>(spec: DocumentContractSpec<M, E>): DocumentContracts<M, E>
export function defineDocumentContracts<const M extends string, const E extends string>(
  spec: DocumentContractSpec<M, E>,
): DocumentContracts<M, E> {
  const { title, titlePlural } = spec
  const name = <V extends string>(verb: V): `${M}.${E}.${V}` => `${spec.module}.${spec.entity}.${verb}`
  const permission = spec.permissions
  const keywords = [title, titlePlural, ...(spec.keywords ?? [])]
  const cost = spec.costPermission === undefined ? PERMISSION_COSTS_READ : spec.costPermission
  const viewFields = cost ? { 'lines.*.costCents': cost } : undefined
  const feature = spec.feature ? { feature: spec.feature } : {}
  const api = spec.publicApi ? { publicApi: true } : {}
  const financial = spec.financial ?? true

  const get = defineContract({
    name: name('get'),
    title: `${title} anzeigen`,
    description: `Zeigt eine ${title} mit Zeilen, Steuern, Belegkette, Verlauf und offenem Posten.`,
    llm: {
      description: `Liefert einen Beleg der Art „${title}“ vollständig: Kopf, Zeilen, Steueraufschlüsselung, Vorgänger und Nachfolger, Statusverlauf, Zahlstand. Verwenden, wenn die Kennung bekannt ist; zum Suchen die Liste nutzen.`,
      keywords,
    },
    input: zIdInput,
    output: zDocumentView,
    permission: permission.read,
    risk: 'read',
    scope: 'record',
    ...(viewFields ? { fieldPermissions: viewFields } : {}),
    ...feature,
    ...api,
  })
  const list = defineContract({
    name: name('list'),
    title: `${titlePlural} anzeigen`,
    description: `Listet ${titlePlural} mit Filtern für Status, Partner, Zeitraum und Standort.`,
    llm: {
      description: `Sucht und listet ${titlePlural}. Filter: Status, Partner, Zeitraum (Belegdatum), Standorte, Suchtext (Nummer, Partnername, Fremdnummer), nur offene Posten. Verwenden für Übersichten und um die Kennung eines Belegs zu finden.`,
      keywords: [...keywords, 'Liste', 'suchen', 'offen'],
    },
    input: zDocumentListInput,
    output: listOutput(zDocumentListRow),
    permission: permission.read,
    risk: 'read',
    scope: 'any',
    ...feature,
    ...api,
  })
  const saveDraft = defineContract({
    name: name('saveDraft'),
    title: `${title} als Entwurf speichern`,
    description: `Legt einen Entwurf an oder ändert ihn. Preise, Steuern und Summen rechnet das System.`,
    llm: {
      description: `Legt eine ${title} als Entwurf an (ohne id) oder ersetzt Kopf und Zeilen eines vorhandenen Entwurfs (mit id). Ohne unitPriceCents schlägt die Preisfindung den Preis vor; Zeilen, die das System selbst führt (Belegrabatt und Ähnliches), entstehen automatisch. Der Entwurf bucht nichts – dafür danach „${title} festschreiben“. Immer ALLE Zeilen senden; fehlende Zeilen werden entfernt.`,
      keywords: [...keywords, 'Entwurf', 'anlegen', 'erfassen', 'ändern'],
    },
    input: zDocumentDraftInput,
    output: zDocumentView,
    permission: permission.write,
    risk: 'write',
    scope: 'record',
    effects: { reversible: true },
    limits: { maxItems: 500, itemCount: (input) => input.lines.length },
    ...(viewFields ? { fieldPermissions: viewFields } : {}),
    ...feature,
    ...api,
  })
  const post = defineContract({
    name: name('post'),
    title: `${title} festschreiben`,
    description: `Schreibt den Entwurf fest: bucht Bestand und offene Posten, vergibt die Belegnummer. Danach ist der Beleg unveränderlich.`,
    llm: {
      description: `Schreibt eine ${title} fest. Bucht die Wirkungen der Belegart (Bestand, Konten, offener Posten), vergibt die lückenlose Nummer und friert den Beleg ein. Nicht umkehrbar außer durch Storno. Nur für Entwürfe; vorher den Entwurf prüfen.`,
      keywords: [...keywords, 'festschreiben', 'buchen', 'abschließen', 'bestätigen'],
    },
    input: zPostInput,
    output: zDocumentView,
    permission: permission.post,
    risk: financial ? 'financial' : 'write',
    scope: 'record',
    effects: { reversible: true },
    ...(viewFields ? { fieldPermissions: viewFields } : {}),
    ...feature,
    ...api,
  })
  const reverse = defineContract({
    name: name('reverse'),
    title: `${title} stornieren`,
    description: `Storniert den Beleg mit einem verknüpften Gegenbeleg; Bestand, Konten und offene Posten werden exakt zurückgenommen.`,
    llm: {
      description: `Storniert eine festgeschriebene ${title}: Es entsteht ein Stornobeleg mit eigener Nummer, alle Buchungen werden exakt zurückgenommen. Braucht einen Grund. Nicht für Teilkorrekturen (dafür Gutschrift bzw. Korrekturbeleg) und nicht, solange Folgebelege bestehen.`,
      keywords: [...keywords, 'Storno', 'stornieren', 'rückgängig', 'zurücknehmen'],
    },
    input: zReverseInput,
    output: zReverseOutput,
    permission: permission.reverse ?? permission.post,
    risk: 'destructive',
    scope: 'record',
    ...(cost ? { fieldPermissions: { 'document.lines.*.costCents': cost } } : {}),
    ...feature,
  })
  const transition = defineContract({
    name: name('transition'),
    title: `Status ändern: ${title}`,
    description: `Wechselt den Status nach den Regeln der Belegart (z. B. schließen, abbrechen) – auch das Schließen eines offenen Rests.`,
    llm: {
      description: `Ändert den Status einer ${title} entlang der erlaubten Übergänge (siehe allowed.transitions des Belegs). Schließt bei „geschlossen“ den offenen Rest: Reservierungen und offene Bestellmengen werden freigegeben. Nicht zum Festschreiben oder Stornieren.`,
      keywords: [...keywords, 'Status', 'schließen', 'abbrechen', 'erledigt'],
    },
    input: zTransitionInput,
    output: zDocumentView,
    permission: permission.transition ?? permission.write,
    risk: 'write',
    scope: 'record',
    ...(viewFields ? { fieldPermissions: viewFields } : {}),
    ...feature,
  })
  const print = defineContract({
    name: name('print'),
    title: `${title} drucken`,
    description: `Liefert die Daten der Druckvorschau: Absender, Empfänger, Positionen, Steuern, Summen, Pflichttexte.`,
    llm: {
      description: `Liefert die strukturierten Druckdaten einer ${title} für die Vorschau (HTML/PDF über den Browser). Verändert nichts. E-Rechnungsdateien entstehen hier nicht.`,
      keywords: [...keywords, 'drucken', 'PDF', 'Vorschau'],
    },
    input: zIdInput,
    output: zDocumentPrintModel,
    permission: permission.print ?? permission.read,
    risk: 'read',
    scope: 'record',
    ...feature,
  })
  const discard = defineContract({
    name: name('discard'),
    title: `Entwurf verwerfen: ${title}`,
    description: `Löscht einen Entwurf. Festgeschriebene Belege lassen sich nicht löschen, nur stornieren.`,
    llm: {
      description: `Löscht den Entwurf einer ${title} endgültig. Nur für Entwürfe – ein festgeschriebener Beleg wird storniert, nicht gelöscht.`,
      keywords: [...keywords, 'verwerfen', 'löschen', 'Entwurf'],
    },
    input: zIdInput,
    output: zDiscardOutput,
    permission: permission.write,
    risk: 'destructive',
    scope: 'record',
    ...feature,
  })
  const derive = spec.derivesFrom?.length
    ? defineContract({
        name: name('derive'),
        title: `${title} aus Vorgängerbeleg anlegen`,
        description: `Legt einen Entwurf aus einem oder mehreren Vorgängerbelegen an – ganz oder in Teilmengen.`,
        llm: {
          description: `Legt eine ${title} als Entwurf aus Vorgängerbelegen an (${spec.derivesFrom.join(', ')}). Ohne Zeilenangabe wird je Zeile die offene Restmenge übernommen; mit lines Teilmengen. Mehrere Vorgänger desselben Partners ergeben einen Sammelbeleg. Der Entwurf bucht nichts.`,
          keywords: [...keywords, 'übernehmen', 'ableiten', 'Teillieferung', 'Sammelrechnung'],
        },
        input: zDocumentDeriveInput,
        output: zDocumentView,
        permission: permission.derive ?? permission.write,
        risk: 'write',
        scope: 'record',
        effects: { reversible: true },
        ...(viewFields ? { fieldPermissions: viewFields } : {}),
        ...feature,
      })
    : null
  const all: ActionContract[] = [get, list, saveDraft, post, reverse, transition, print, discard, ...(derive ? [derive] : [])]
  return Object.freeze({
    documentType: spec.documentType,
    title,
    titlePlural,
    permissions: Object.freeze({
      read: permission.read,
      write: permission.write,
      post: permission.post,
      reverse: permission.reverse ?? permission.post,
      transition: permission.transition ?? permission.write,
      derive: permission.derive ?? permission.write,
      print: permission.print ?? permission.read,
    }),
    get,
    list,
    saveDraft,
    post,
    reverse,
    transition,
    print,
    discard,
    derive,
    all,
  })
}
